/**
 * Склады проекта на обзоре карточки (СК-01, поток 5): список складов с переходом в карточку склада,
 * кнопка «Склад объекта» заводит приобъектный склад (`POST /warehouse/warehouses`), ссылка в раздел «Склад».
 * Видно сотрудникам с правом «Видеть склад»; заводит кладовщик проекта («Проводить складские операции»).
 */
import { can, type ProjectDetail, WAREHOUSE_KINDS, type Warehouse } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { api } from '../../lib/api';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { warehousesQuery } from '../warehouse/queries';

export function WarehousePanel({
  project,
  target,
}: {
  project: ProjectDetail;
  target: {
    legalEntityId: string;
    directionId: string | null;
    projectId: string;
    projectPath: Array<string | null>;
  };
}) {
  const me = useMe();
  const qc = useQueryClient();
  const staff = Boolean(me.tenant?.staff);
  const sees = staff && can(me.access, 'warehouse.view', target);
  const operates = staff && can(me.access, 'warehouse.operate', target);
  const q = useQuery({ ...warehousesQuery, enabled: sees });
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [objectId, setObjectId] = useState('');
  const create = useMutation({
    mutationFn: () =>
      api<Warehouse>('/warehouse/warehouses', {
        body: { kind: 'site', name: name.trim(), projectId: project.id, objectId: objectId || null },
      }),
    onSuccess: (w) => {
      qc.invalidateQueries({ queryKey: ['wh'] });
      setOpen(false);
      setName('');
      setObjectId('');
      toast(`Склад «${w.name}» заведён`);
    },
  });
  if (!sees) return null;
  const mine = (q.data ?? []).filter((w) => w.projectId === project.id && w.isActive);

  function submit(e: FormEvent) {
    e.preventDefault();
    create.mutate();
  }

  return (
    <Panel
      title="Склады проекта"
      meta={mine.length ? `${mine.length}` : undefined}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/warehouse"
            search={{ tab: 'warehouses' }}
            className="inline-flex h-11 items-center px-2 text-[13px] font-medium text-ink underline-offset-4 hover:underline md:h-8"
          >
            Раздел «Склад»
          </Link>
          {operates ? (
            <Button
              size="sm"
              icon={<Plus className="size-4" />}
              onClick={() => {
                setName(`Склад объекта ${project.code}`);
                setOpen(true);
              }}
            >
              Склад объекта
            </Button>
          ) : null}
        </div>
      }
      bodyClassName={mine.length ? 'p-0' : undefined}
    >
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : mine.length ? (
        <ul className="divide-y divide-line">
          {mine.map((w) => (
            <li key={w.id}>
              <Link
                to="/warehouse/warehouses/$warehouseId"
                params={{ warehouseId: w.id }}
                className="flex min-h-11 flex-col gap-1 px-5 py-2.5 hover:bg-surface-2 md:flex-row md:items-center md:gap-3"
              >
                <span className="min-w-0 flex-1 text-[14px] font-medium text-ink [overflow-wrap:anywhere]">
                  {w.name}
                </span>
                <span className="flex flex-wrap items-center gap-x-2 text-[12px] text-ink-2">
                  <span>
                    {WAREHOUSE_KINDS[w.kind]}
                    {w.objectName ? ` · ${w.objectName}` : ''}
                    {w.molName ? ` · МОЛ ${w.molName}` : ''}
                  </span>
                  <span className="num">{w.itemsInStock ? `${w.itemsInStock} поз.` : 'пусто'}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[14px] text-ink-2">
          Складов у проекта пока нет. Приобъектный склад заводит кладовщик проекта; материалы, выданные людям
          на работы, и давальческие у подрядчиков видны в разделе «Склад».
        </p>
      )}
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title="Склад объекта"
        description="Приобъектный склад проекта: остатки, выдача на работы, инвентаризация. МОЛ и кладовщиков назначают в карточке склада."
        width="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Отмена
            </Button>
            <Button variant="primary" type="submit" form="site-warehouse" loading={create.isPending}>
              Завести склад
            </Button>
          </>
        }
      >
        <form id="site-warehouse" onSubmit={submit} className="flex flex-col gap-4" noValidate>
          <Field label="Название" htmlFor="wh-name">
            <Input
              id="wh-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={200}
            />
          </Field>
          {project.objects.length ? (
            <Field label="Объект" htmlFor="wh-object" hint="Можно не указывать — склад на весь проект">
              <Select id="wh-object" value={objectId} onChange={(e) => setObjectId(e.target.value)}>
                <option value="">Весь проект</option>
                {project.objects.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          <ErrorBox error={create.error} />
        </form>
      </Dialog>
    </Panel>
  );
}
